/**
 * Admin Guard
 * Protects routes that require platform admin access.
 * Checks /users/{uid}.isPlatformAdmin === true
 *
 * This is SEPARATE from team-level roles (owner/admin/member).
 */

import { ReactNode } from 'react';
import { usePlatformAdmin } from '@/hooks/useGlobalAdmin';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AdminGuardProps {
  children: ReactNode;
  /** Where to redirect non-admins (default: /dashboard) */
  redirectTo?: string;
}

export function AdminGuard({ children, redirectTo = '/dashboard' }: AdminGuardProps) {
  const { isLoading: authLoading } = useAuth();
  const { isPlatformAdmin, isLoading: adminLoading } = usePlatformAdmin();

  // Show loading state while checking auth and admin status
  if (authLoading || adminLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Block non-admins
  if (!isPlatformAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4 max-w-md p-6">
          <ShieldAlert className="h-12 w-12 mx-auto text-destructive" />
          <h2 className="text-xl font-semibold">Admin Access Required</h2>
          <p className="text-sm text-muted-foreground">
            This page requires platform administrator privileges.
          </p>
          <Button asChild variant="outline" className="mt-4">
            <a href={redirectTo}>Go to Dashboard</a>
          </Button>
        </div>
      </div>
    );
  }

  // User is platform admin - render children
  return <>{children}</>;
}
